import React from 'react'

import {
  CONVERSATION_FACTS_DIMENSIONS,
  useCubeReportAccessFiltersContext,
  useCubeReportPageFiltersContext,
} from 'cube'
import { MedianConversationLengthDrillDownBase } from 'cube/queries/MedianConversationLengthDrillDown.query'
import { equals } from 'ramda'

import { Link } from '@driftt/dds'
import {
  CubeQueryBuilder,
  CubeQueryStringAdapter,
  KPICard,
} from '@driftt/dds.reports'

import { useTrackDrillDownClickEvent } from 'components/conversation/hooks/useTrackDrilldownClickEvent'
import useTimePeriod from 'hooks/useTimePeriod'
import { CONVO_DETAIL_REPORT_PATH } from 'layouts/ConversationsDetailReport/ConversationsDetailReport.constants'
import { getHumanizedDurationMilliseconds } from 'utils/format'

import useConversationCountForMedian from './useConversationCountForMedian.hook'
import useMedianConversationLength from './useMedianConversationLength.hook'

export const MedianConversationLengthKPICard = () => {
  const { metric, previousMetric, isLoading, hasNoData, hasError } =
    useMedianConversationLength()

  const { resultCount } = useConversationCountForMedian()
  const timePeriod = useTimePeriod()
  const { pageFilters } = useCubeReportPageFiltersContext()
  const { accessFilters } = useCubeReportAccessFiltersContext()

  const { trackViewEvent } = useTrackDrillDownClickEvent(
    'ConversationLengthKPICard'
  )

  const drillDownCubeQuery = new CubeQueryBuilder({
    query: MedianConversationLengthDrillDownBase,
  })
    .withFilters([...pageFilters, ...accessFilters])
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      timePeriod?.startDate.toDate(),
      timePeriod?.endDate.toDate()
    )
    .build()

  const drillDownQueryString =
    CubeQueryStringAdapter.toQueryString(drillDownCubeQuery)

  return (
    <KPICard
      metric={metric}
      previousMetric={previousMetric}
      formatMetric={(medianConversationLengthSeconds: number) =>
        getHumanizedDurationMilliseconds(medianConversationLengthSeconds * 1000)
      }
      isLoading={isLoading}
      hasNoData={hasNoData || equals(resultCount, 0)}
      hasError={hasError}
    >
      <KPICard.Header
        title="Overall median chat length"
        description="How long is it from first open to first close?"
      />
      <KPICard.Content>
        <KPICard.Metric />
        <Link
          variant="inline"
          target="__blank"
          href={`${CONVO_DETAIL_REPORT_PATH}?${drillDownQueryString}`}
          onClick={() => trackViewEvent({ totalRecords: metric ?? 0 })}
        >
          View data
        </Link>
      </KPICard.Content>
    </KPICard>
  )
}
